import { Job, ZipRecruiterFields } from '../types';

export const zipRecruiterMinCompanyTextLength = 15;

export const isValidZipRecruiterCompanyText = (value?: string): boolean =>
  (value?.trim().length ?? 0) >= zipRecruiterMinCompanyTextLength;

export const zipRecruiterEmploymentTypeOptions = [
  { label: 'Full-Time', value: 'Full-Time' },
  { label: 'Part-Time', value: 'Part-Time' },
  { label: 'Per Diem', value: 'Per Diem' },
  { label: 'Contractor', value: 'Contractor' },
  { label: 'Temporary', value: 'Temporary' },
  { label: 'Other', value: 'Other' },
] as const;

export const resolveZipRecruiterEmploymentType = (value?: string): string | undefined => {
  if (!value) {
    return undefined;
  }

  const normalized = value.trim().toLowerCase().replace(/\s+/g, ' ');
  const aliases: Record<string, string> = {
    'full-time': 'Full-Time',
    'full time': 'Full-Time',
    'fulltime': 'Full-Time',
    'part-time': 'Part-Time',
    'part time': 'Part-Time',
    'parttime': 'Part-Time',
    contract: 'Contractor',
    contractor: 'Contractor',
    temporary: 'Temporary',
    temp: 'Temporary',
    'per diem': 'Per Diem',
    other: 'Other',
    internship: 'Other',
  };

  if (aliases[normalized]) {
    return aliases[normalized];
  }

  const match = zipRecruiterEmploymentTypeOptions.find(
    option => option.value.toLowerCase() === normalized,
  );

  return match?.value ?? value.trim();
};

export const zipRecruiterPayTypeOptions = [
  { label: 'Pay range', value: 'Pay range' },
  { label: 'Exact amount', value: 'Exact amount' },
] as const;

export const zipRecruiterPayCurrencyOptions = [{ label: 'USD', value: 'USD' }] as const;

export const zipRecruiterPayFrequencyOptions = [
  { label: 'Annually', value: 'Annually' },
  { label: 'Hourly', value: 'Hourly' },
  { label: 'Monthly', value: 'Monthly' },
  { label: 'Weekly', value: 'Weekly' },
] as const;

export const zipRecruiterPreferredDistanceOptions = [10, 25, 50, 75, 100] as const;

export const resolveZipRecruiterPreferredDistance = (distance: number): number => {
  const validDistances = zipRecruiterPreferredDistanceOptions as readonly number[];

  if (validDistances.includes(distance)) {
    return distance;
  }

  return validDistances.reduce((closest, candidate) =>
    Math.abs(candidate - distance) < Math.abs(closest - distance) ? candidate : closest,
  );
};

export const zipRecruiterRemoteJobOptions = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
] as const;

export const zipRecruiterBenefitOptions = [
  { label: 'Medical insurance', value: 'medical' },
  { label: 'Dental insurance', value: 'dental' },
  { label: 'Vision insurance', value: 'vision' },
  { label: 'Retirement', value: 'retirement' },
  { label: 'Life insurance', value: 'life_insurance' },
  { label: 'Paid time off', value: 'pto' },
  { label: 'None of the above', value: 'none' },
] as const;

export const zipRecruiterBenefitValues = zipRecruiterBenefitOptions
  .map(option => option.value)
  .filter(value => value !== 'none');

const mapLocationTypeToRemoteJob = (locationType?: string): boolean | undefined => {
  switch (locationType) {
    case 'Remote':
      return true;
    case 'OnSite':
    case 'Hybrid':
      return false;
    default:
      return undefined;
  }
};

const mapJobTypeToZipEmploymentType = (jobType?: string): string | undefined => {
  switch (jobType) {
    case 'FullTime':
    case 'Permanent':
      return 'Full-Time';
    case 'PartTime':
      return 'Part-Time';
    case 'Contract':
      return 'Contractor';
    case 'Temporary':
      return 'Temporary';
    default:
      return undefined;
  }
};

const parseSalaryRange = (salary?: string): { payMinimum?: number; payMaximum?: number } => {
  if (!salary) {
    return {};
  }

  const numbers = salary.match(/\d[\d,]*/g)?.map(value => Number(value.replace(/,/g, ''))) || [];

  if (numbers.length === 0) {
    return {};
  }

  if (numbers.length === 1) {
    return { payMinimum: numbers[0], payMaximum: numbers[0] };
  }

  return { payMinimum: Math.min(...numbers), payMaximum: Math.max(...numbers) };
};

export const buildZipRecruiterFieldsFromJob = (job: Partial<Job>): ZipRecruiterFields => {
  const saved = job.zipRecruiterFields || {};
  const salaryRange = parseSalaryRange(job.salary);

  return {
    jobLocation: saved.jobLocation ?? job.location ?? '',
    streetAddress: saved.streetAddress ?? '',
    isRemoteJob: saved.isRemoteJob ?? mapLocationTypeToRemoteJob(job.locationType),
    includeWillingToRelocate: saved.includeWillingToRelocate ?? false,
    preferredDistance: saved.preferredDistance,
    employmentType:
      resolveZipRecruiterEmploymentType(saved.employmentType) ??
      mapJobTypeToZipEmploymentType(job.jobType),
    payType: saved.payType ?? 'Pay range',
    payMinimum: saved.payMinimum ?? salaryRange.payMinimum,
    payMaximum: saved.payMaximum ?? salaryRange.payMaximum,
    payCurrency: saved.payCurrency ?? 'USD',
    payFrequency: saved.payFrequency ?? 'Annually',
    payCommission: saved.payCommission ?? false,
    benefits: saved.benefits ?? [],
    hiringCompanyName: saved.hiringCompanyName ?? job.clientName ?? '',
    hiringCompanyWhyWorkHere: saved.hiringCompanyWhyWorkHere ?? '',
    hiringCompanyDescription: saved.hiringCompanyDescription ?? '',
    resumeNotRequired: saved.resumeNotRequired ?? false,
  };
};

export const emptyZipRecruiterFields = (): ZipRecruiterFields => ({
  jobLocation: '',
  streetAddress: '',
  includeWillingToRelocate: false,
  payType: 'Pay range',
  payCurrency: 'USD',
  payFrequency: 'Annually',
  payCommission: false,
  benefits: [],
  hiringCompanyName: '',
  hiringCompanyWhyWorkHere: '',
  hiringCompanyDescription: '',
  resumeNotRequired: false,
});

export type ZipRecruiterAutofillPayload = ZipRecruiterFields & {
  jobId?: string;
  jobTitle: string;
  jobDescription: string;
  clientName: string;
};

export const buildZipRecruiterAutofillPayload = (job: Partial<Job>): ZipRecruiterAutofillPayload => ({
  ...buildZipRecruiterFieldsFromJob(job),
  jobId: job._id,
  jobTitle: job.title || '',
  jobDescription: job.description || '',
  clientName: job.clientName || '',
});
